/**
 * ScanPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Camera,
  X,
  Search,
  Plus,
  PlusCircle,
  ArrowLeft,
  MapPin,
  Package
} from 'lucide-react';
import { inventory, locations as locationsApi } from '../services/api';

function ScanPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [mode, setMode] = useState('idle'); // idle, scanning, manual, result, duplicate
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    locationsApi.list()
      .then(data => setLocations(data.locations || []))
      .catch(() => {});

    // Cleanup camera on unmount
    return () => stopCamera();
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = async () => {
    setMode('scanning');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Dynamic import Quagga for barcode detection
      try {
        const Quagga = (await import('@quagga/quagga2')).default;
        Quagga.init({
          inputStream: {
            type: 'LiveStream',
            target: videoRef.current,
            constraints: { facingMode: 'environment' }
          },
          decoder: {
            readers: ['ean_reader', 'ean_8_reader', 'upc_reader', 'upc_e_reader']
          }
        }, (err) => {
          if (err) {
            console.error('[Scan] Quagga init error:', err);
            return;
          }
          Quagga.start();
        });

        Quagga.onDetected((result) => {
          if (result?.codeResult?.code) {
            Quagga.stop();
            stopCamera();
            handleBarcodeLookup(result.codeResult.code);
          }
        });
      } catch {
        // Quagga not available — camera preview still works, user can use manual entry
        console.log('[Scan] Barcode scanner not available, manual entry fallback');
      }
    } catch {
      toast.error(t('scan.cameraError'));
      setMode('manual');
    }
  };

  const handleBarcodeLookup = async (barcode) => {
    setLoading(true);
    try {
      const result = await inventory.scan(barcode);
      setScanResult(result);

      if (result.duplicate) {
        setMode('duplicate');
      } else if (result.found) {
        setMode('result');
        if (result.suggested_location) {
          setSelectedLocation(String(result.suggested_location.id));
        }
      } else {
        setMode('result');
      }
    } catch {
      toast.error(t('common.error'));
      setMode('idle');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualBarcode.trim()) return;
    stopCamera();
    handleBarcodeLookup(manualBarcode.trim());
  };

  const handleAddToInventory = async () => {
    if (!scanResult?.product) return;
    setLoading(true);

    try {
      await inventory.confirmScan({
        product_id: scanResult.product.id,
        action: 'new',
        quantity: 1,
        unit: 'piece',
        location_id: selectedLocation ? parseInt(selectedLocation) : undefined
      });

      toast.success(t('scan.added', { name: scanResult.product.name }));
      resetScan();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateAction = async (action, existingItemId) => {
    if (!scanResult?.product) return;
    setLoading(true);

    try {
      await inventory.confirmScan({
        product_id: scanResult.product.id,
        action,
        existing_item_id: existingItemId,
        quantity: 1
      });

      const actionLabel = action === 'increment' ? t('scan.increment') : t('scan.addNew');
      toast.success(`${actionLabel}: ${scanResult.product.name}`);
      resetScan();
    } catch {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const resetScan = () => {
    setScanResult(null);
    setSelectedLocation('');
    setManualBarcode('');
    setMode('idle');
  };

  return (
    <div className="scan-page" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => { stopCamera(); navigate(-1); }}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ flex: 1, margin: 0 }}>{t('scan.title')}</h1>
      </div>

      <div style={{ padding: '0 var(--space-md)' }}>

        {/* === IDLE STATE === */}
        {mode === 'idle' && (
          <div className="empty-state">
            <Camera size={64} className="empty-state-icon" />
            <h3>{t('scan.title')}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', marginTop: 'var(--space-lg)' }}>
              <button className="btn btn-primary btn-lg btn-full" onClick={startCamera}>
                <Camera size={20} /> {t('scan.scanning')}
              </button>
              <button className="btn btn-secondary btn-lg btn-full" onClick={() => setMode('manual')}>
                <Search size={20} /> {t('scan.manualEntry')}
              </button>
            </div>
          </div>
        )}

        {/* === SCANNING STATE === */}
        {mode === 'scanning' && (
          <div>
            <div style={{
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              background: '#000',
              marginBottom: 'var(--space-md)',
              position: 'relative',
              aspectRatio: '4/3'
            }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {/* Scanning overlay */}
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <div style={{
                  width: '250px', height: '100px',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  opacity: 0.7
                }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button className="btn btn-secondary btn-full" onClick={() => { stopCamera(); setMode('manual'); }}>
                {t('scan.manualEntry')}
              </button>
              <button className="btn btn-ghost btn-full" onClick={() => { stopCamera(); setMode('idle'); }}>
                {t('scan.stopCamera')}
              </button>
            </div>
          </div>
        )}

        {/* === MANUAL ENTRY STATE === */}
        {mode === 'manual' && (
          <div className="card">
            <h3 className="card-title mb-md">{t('scan.manualEntry')}</h3>
            <form onSubmit={handleManualSubmit}>
              <div className="form-group">
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('scan.manualPlaceholder')}
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  autoFocus
                  inputMode="numeric"
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading || !manualBarcode.trim()}>
                  {loading ? t('common.loading') : t('scan.lookup')}
                </button>
                <button type="button" className="btn btn-ghost" onClick={resetScan}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* === RESULT STATE (product found or not found) === */}
        {mode === 'result' && scanResult && (
          <div className="card">
            {scanResult.found ? (
              <>
                <div className="flex items-center gap-md mb-md">
                  {scanResult.product.image_url ? (
                    <img
                      src={scanResult.product.image_url}
                      alt={scanResult.product.name}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                    />
                  ) : (
                    <div className="list-item-icon" style={{ width: 64, height: 64, background: 'var(--color-primary-bg)' }}>
                      <Package size={28} color="var(--color-primary)" />
                    </div>
                  )}
                  <div>
                    <h3 style={{ margin: 0 }}>{scanResult.product.name}</h3>
                    {scanResult.product.brand && (
                      <p className="text-sm text-secondary" style={{ margin: 0 }}>{scanResult.product.brand}</p>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <MapPin size={14} style={{ verticalAlign: 'middle' }} /> {t('scan.selectLocation')}
                  </label>
                  <select
                    className="form-select"
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                  >
                    <option value="">{t('scan.selectLocation')}</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                  {scanResult.suggested_location && (
                    <p className="text-xs text-secondary mt-sm">
                      {t('putAway.suggested', { location: scanResult.suggested_location.name })}
                    </p>
                  )}
                </div>

                <button
                  className="btn btn-primary btn-full btn-lg"
                  onClick={handleAddToInventory}
                  disabled={loading}
                >
                  <Plus size={18} />
                  {loading ? t('common.loading') : t('scan.addToInventory')}
                </button>
              </>
            ) : (
              <div className="text-center">
                <p className="mb-md">{t('scan.notFound')}</p>
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <button className="btn btn-primary btn-full" onClick={() => navigate('/add-item')}>
                    <Plus size={18} /> {t('dashboard.addItem')}
                  </button>
                  <button className="btn btn-secondary btn-full" onClick={resetScan}>
                    {t('scan.cancel')}
                  </button>
                </div>
              </div>
            )}

            <button className="btn btn-ghost btn-full mt-md" onClick={resetScan}>
              {t('scan.cancel')}
            </button>
          </div>
        )}

        {/* === DUPLICATE STATE === */}
        {mode === 'duplicate' && scanResult && (
          <div className="card">
            <div className="banner banner-info mb-md">
              {t('scan.duplicateMessage', { name: scanResult.product?.name })}
            </div>

            <h3 className="mb-md">{t('scan.duplicate')}</h3>

            {scanResult.existing_items?.map(item => (
              <div key={item.id} className="list-item mb-sm">
                <div className="list-item-content">
                  <div className="list-item-title">{scanResult.product.name}</div>
                  <div className="list-item-subtitle">
                    {item.quantity} {item.unit}
                    {item.location_name && ` — ${item.location_name}`}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => handleDuplicateAction('increment', item.id)}
                  disabled={loading}
                >
                  <PlusCircle size={16} /> {t('scan.increment')}
                </button>
              </div>
            ))}

            <div className="divider" />

            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                className="btn btn-primary btn-full"
                onClick={() => handleDuplicateAction('new')}
                disabled={loading}
              >
                <Plus size={18} /> {t('scan.addNew')}
              </button>
              <button className="btn btn-ghost btn-full" onClick={resetScan}>
                {t('scan.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Loading overlay */}
        {loading && mode !== 'idle' && (
          <div style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
            <div className="loader-spinner" style={{ margin: '0 auto' }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ScanPage;
